export type PostSection = {
  heading: string
  paragraphs: string[]
  bullets?: string[]
}

export type BlogPost = {
  slug: string
  title: string
  excerpt: string
  category: string
  date: string
  readingMinutes: number
  author: string
  standfirst: string
  body: PostSection[]
}

export const blogPosts: BlogPost[] = [
  {
    slug: 'late-payment-culture-costing-smes',
    title: 'What the UK’s late payment culture is really costing your business',
    excerpt:
      'Beyond the headline figures, late payment quietly drains management time, delays hiring and stalls growth. Here is how to measure the true cost in your own ledger — and where to intervene first.',
    category: 'Cash flow',
    date: '2026-08-19',
    readingMinutes: 7,
    author: 'Credit Manager team',
    standfirst:
      'The national statistics are stark, but they are not the number that matters. The number that matters is the one sitting in your own aged debtors report.',
    body: [
      {
        heading: 'The cost you can see',
        paragraphs: [
          'Most owners can tell you roughly what they are owed. Far fewer can tell you what that money not arriving on time actually costs them, because the cost rarely appears as a single line in the accounts.',
          'Start with the obvious: the working capital tied up in unpaid invoices. If your average debtor days sit at 60 rather than the 30 on your terms, you are permanently funding a month of trading on your customers’ behalf.',
        ],
      },
      {
        heading: 'The cost you cannot see',
        paragraphs: [
          'The larger cost is usually time. Chasing is skilled, uncomfortable work, and it tends to land on whoever is most senior enough to be taken seriously — which is exactly the person whose time is worth the most.',
        ],
        bullets: [
          'Management hours spent on reminders, calls and disputes rather than on customers or delivery.',
          'Hiring, equipment and marketing decisions deferred because the cash position is uncertain.',
          'Discounts, factoring fees or overdraft interest accepted to bridge a gap that should not exist.',
          'Relationships strained by conversations nobody wanted to have.',
        ],
      },
      {
        heading: 'How to measure it in your own ledger',
        paragraphs: [
          'Pull your aged debtors report and work out three figures: your average debtor days, the proportion of your ledger past terms, and the value written off in the last twelve months. Those three tell you more than any national survey.',
          'Then estimate the hours your team spends chasing in a typical month, and price them at their real cost. Most businesses are surprised by the total — and it is usually a multiple of what better process would cost.',
        ],
      },
      {
        heading: 'Where to intervene first',
        paragraphs: [
          'Two changes move the needle faster than anything else. First, check credit properly before offering terms, so fewer bad accounts enter the ledger at all. Second, automate the reminder sequence so chasing happens consistently on day one rather than when someone finds time.',
          'Neither requires a change of accounting software or a new finance hire. Both simply require the process to run without depending on anyone remembering.',
        ],
      },
    ],
  },
  {
    slug: 'credit-checks-before-offering-terms',
    title: 'The five credit checks to run before you offer a customer terms',
    excerpt:
      'Offering credit is lending money. A short, repeatable check on legal entity, score, limit, filings and directors will stop most bad debt before the first invoice is raised.',
    category: 'Credit risk',
    date: '2026-08-05',
    readingMinutes: 6,
    author: 'Credit Manager team',
    standfirst:
      'Every time you invoice in arrears you are extending an unsecured loan. These five checks take minutes and prevent most of the losses that follow.',
    body: [
      {
        heading: 'Why this happens at the wrong moment',
        paragraphs: [
          'Credit decisions are usually made in the middle of a sales conversation, when the incentive is to say yes. Making the check a fixed step in onboarding removes the judgement call from the moment of pressure.',
        ],
      },
      {
        heading: 'The five checks',
        paragraphs: [
          'None of these require specialist knowledge, and all of them can be run from a single credit report.',
        ],
        bullets: [
          'Legal entity — confirm the exact registered company you are contracting with, not the trading name on the email signature.',
          'Credit score and band — the summary view of how likely this business is to default.',
          'Recommended credit limit — if the customer is asking for more than the recommendation, that is a decision, not a formality.',
          'Filings and proceedings — late accounts, CCJs or winding-up petitions are the clearest possible warning.',
          'Directors and shareholders — check for prior insolvencies and for directors appearing across several failed entities.',
        ],
      },
      {
        heading: 'Set the limit, then monitor it',
        paragraphs: [
          'A credit check is a snapshot. Circumstances change, and the customer who was sound at onboarding may not be in eighteen months.',
          'Alerts on score changes, new proceedings, director changes and address changes turn a one-off check into ongoing monitoring, so limits can be adjusted before exposure becomes a loss.',
        ],
      },
    ],
  },
  {
    slug: 'writing-chaser-emails-that-work',
    title: 'Writing chaser emails people actually reply to',
    excerpt:
      'Tone matters more than frequency. A look at the sequence structure, subject lines and escalation points that consistently shorten debtor days without damaging the relationship.',
    category: 'Credit control',
    date: '2026-07-22',
    readingMinutes: 5,
    author: 'Credit Manager team',
    standfirst:
      'Sending more reminders rarely works. Sending better ones, on a predictable schedule, almost always does.',
    body: [
      {
        heading: 'Start before the due date',
        paragraphs: [
          'The most effective message in any sequence is the one sent a few days before payment is due. It is not a chase, it carries no friction, and it removes the most common excuse — that the invoice was never seen.',
        ],
      },
      {
        heading: 'Structure the sequence',
        paragraphs: [
          'A sequence should escalate in tone, not volume. Five well-spaced messages that each change register work better than fifteen identical ones.',
        ],
        bullets: [
          'Pre-due reminder — friendly, informational, includes the invoice and payment link.',
          'Due date — short and neutral.',
          'Seven days past — direct, states the terms that were agreed.',
          'Fourteen days past — firm, references late payment charges.',
          'Final notice — states clearly that the next step is escalation to a collections agency.',
        ],
      },
      {
        heading: 'Send it from your own domain',
        paragraphs: [
          'A reminder from your own address carries more weight than one from a platform domain, and is far less likely to be filtered into junk. If your software allows it, use it.',
          'Keep subject lines factual and specific — the invoice number and amount outperform anything urgent-sounding.',
        ],
      },
    ],
  },
  {
    slug: 'when-to-escalate-to-a-collections-agency',
    title: 'When to stop chasing and escalate to a collections agency',
    excerpt:
      'There is a point where more reminders cost you more than they recover. How to set an objective escalation trigger, and what to expect from a no win, no fee recovery process.',
    category: 'Collections',
    date: '2026-07-08',
    readingMinutes: 6,
    author: 'Credit Manager team',
    standfirst:
      'Escalation is often treated as a last resort taken too late. Treated as a defined step, it recovers more and costs less.',
    body: [
      {
        heading: 'The signal to escalate',
        paragraphs: [
          'The trigger is not a feeling, it is a pattern: silence, or promises that are not kept. Once a customer has stopped responding or has broken two payment commitments, further reminders from you are unlikely to change the outcome.',
          'Setting the trigger in advance — a fixed number of days past terms — removes the hesitation that lets debts age past the point of easy recovery.',
        ],
      },
      {
        heading: 'What escalation actually changes',
        paragraphs: [
          'Involving a third party does three things at once, and all of them help.',
        ],
        bullets: [
          'It signals that you are serious, which most customers respond to immediately.',
          'It moves you up their list of creditors.',
          'It removes the emotion, and the relationship, from the conversation.',
        ],
      },
      {
        heading: 'What to expect commercially',
        paragraphs: [
          'A no win, no fee arrangement means the agency is paid a percentage of what it recovers — typically 10% plus VAT — and nothing if it recovers nothing.',
          'In some circumstances statutory interest and compensation can be reclaimed alongside the debt, which can offset the fee entirely.',
        ],
      },
    ],
  },
  {
    slug: 'invoice-finance-explained',
    title: 'Selective invoice finance, explained without the jargon',
    excerpt:
      'Borrowing against unpaid invoices is often misunderstood. What it costs, when it is the right call, and how it compares with an overdraft or waiting on a payment run.',
    category: 'Funding',
    date: '2026-06-24',
    readingMinutes: 8,
    author: 'Credit Manager team',
    standfirst:
      'Invoice finance has a reputation problem, mostly earned by older whole-ledger facilities. Selective finance works differently.',
    body: [
      {
        heading: 'What it is',
        paragraphs: [
          'Selective invoice finance lets you draw funds against specific unpaid invoices, rather than committing your entire sales ledger to a facility. You choose which invoices, and when.',
          'Advance rates commonly reach up to 90% of the invoice value, with the balance released when your customer pays.',
        ],
      },
      {
        heading: 'When it is the right call',
        paragraphs: [
          'It suits a timing problem rather than a profitability problem. If the work is done, the invoice is sound, and the only issue is that payment lands after your own obligations, it bridges the gap cleanly.',
        ],
        bullets: [
          'Payroll or supplier payments falling due before a large invoice settles.',
          'A growth opportunity that needs stock or staff ahead of revenue.',
          'A single large customer whose payment run sits well beyond your terms.',
        ],
      },
      {
        heading: 'How it compares',
        paragraphs: [
          'An overdraft is cheaper when it is available, but limits are fixed and rarely scale with a growing ledger. Waiting costs nothing in fees but can cost a great deal in missed opportunity.',
          'The honest comparison is between the cost of the advance and the cost of the delay. Price both before deciding.',
        ],
      },
    ],
  },
  {
    slug: 'late-payment-charges-fsb',
    title: 'Should you charge interest on late payments?',
    excerpt:
      'Most UK SMEs never apply the late payment charges they are legally entitled to. A practical look at statutory interest, compensation, and how to raise it without a confrontation.',
    category: 'Credit control',
    date: '2026-06-10',
    readingMinutes: 5,
    author: 'Credit Manager team',
    standfirst:
      'The right to charge interest exists precisely because late payment has a cost. Very few businesses use it.',
    body: [
      {
        heading: 'What you are entitled to',
        paragraphs: [
          'On commercial debts you are generally entitled to statutory interest plus a fixed sum in compensation, unless your contract sets out a substantial alternative remedy.',
          'The point is rarely the revenue. The point is that terms with no consequence are not terms.',
        ],
      },
      {
        heading: 'Why most businesses never charge it',
        paragraphs: [
          'The fear is always the same: that raising it will damage the relationship or cost the account. In practice, the damage comes from raising it inconsistently and personally.',
        ],
      },
      {
        heading: 'How to apply it without a confrontation',
        paragraphs: [
          'State it in your terms, reference it in the standard reminder sequence, and apply it by process rather than by decision. When the charge is automatic and expected, it stops being a personal challenge.',
          'Reserve waiving it as a deliberate goodwill gesture — which only carries weight if the charge existed in the first place.',
        ],
      },
    ],
  },
  {
    slug: 'connecting-your-accounting-software',
    title: 'Getting the most from your accounting software connection',
    excerpt:
      'Your ledger is only as useful as the data in it. Cleaning up contacts, terms and entity names before you connect makes automated chasing dramatically more effective.',
    category: 'Getting started',
    date: '2026-05-27',
    readingMinutes: 4,
    author: 'Credit Manager team',
    standfirst:
      'Automation amplifies whatever is already in your ledger — including the gaps. A short tidy-up first pays for itself immediately.',
    body: [
      {
        heading: 'Fix the contacts',
        paragraphs: [
          'Automated chasing fails most often because reminders go to the wrong person. Before connecting, make sure every customer record has a genuine accounts payable contact rather than the salesperson you originally dealt with.',
        ],
      },
      {
        heading: 'Confirm the legal entities',
        paragraphs: [
          'Trading names and registered names diverge more often than people expect. Matching every customer to its Companies House entity makes credit scoring accurate and protects you if a debt is ever disputed.',
        ],
      },
      {
        heading: 'Set terms explicitly',
        paragraphs: [
          'Blank or inconsistent payment terms make it impossible to know what is actually overdue. Set them per customer so the reminder schedule has something reliable to work from.',
        ],
        bullets: [
          'Give every customer explicit terms rather than relying on a default.',
          'Remove duplicate customer records before the first sync.',
          'Close off historic invoices that will never be paid, so the aged report reflects reality.',
        ],
      },
    ],
  },
]

export function findPost(slug: string) {
  return blogPosts.find((post) => post.slug === slug)
}

export function formatPostDate(date: string) {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
